import styled from 'styled-components';
import {useCreateTransaction} from '@/api/payments';
import {getGqlErrorMessage, isAccessRestrictionError} from '@/api/request';
import {Button} from '@/components/Button';
import toast from '@/components/HotToast';
import {Typography} from '@/components/Typography';
import {
  PaymentActionType,
  PaymentTransactionStatus,
  PaymentType,
  ProductType,
} from '@/enums';
import {useSession, useTossRequestBillingAuth} from '@/hooks';
import {
  BackgroundImage,
  DashRoundedBox,
  FlexFullGrow,
  GrayRoundedBox,
  interceptProps,
} from '@/styled-components';
import {palette} from '@/styles';
import {getCardLogo, getCardName, getCardShortNumber} from '@/utils';
import DefaultCard from './assets/icon-card.png';
import {Box} from './StyledComponents';

export const PayMethod = () => {
  const {user} = useSession();
  const isEmpty = !user?.billing?.cardNumber;
  const customerKey = user?.id;
  const {mutate: createTransaction, isPending} = useCreateTransaction();
  const requestBillingAuth = useTossRequestBillingAuth(
    customerKey,
    PaymentActionType.method,
  );

  const changeCardInfo = () => {
    if (!customerKey || !user.billing?.productId) {
      toast.error(
        '고객 정보 혹은 결제 정보가 없습니다. 고객센터로 문의해 주세요.',
      );
      return;
    }

    try {
      createTransaction(
        {
          customerKey,
          product: user.billing.productId,
          method: '카드',
        },
        {
          onSuccess: (data) => {
            const transaction = data.result;

            if (
              transaction?.type === PaymentType.Billing &&
              transaction?.status === PaymentTransactionStatus.PREPARE
            ) {
              requestBillingAuth(transaction);
            } else {
              toast.error(
                '카드 변경 요청이 실패했습니다. 잠시 후 다시 시도해주세요. (트랜잭션 생성 실패)',
              );
            }
          },
          onError: (error) => {
            // 이용 제한은 공통 에러 핸들러가 안내 모달을 띄운다.
            if (!isAccessRestrictionError(error)) {
              toast.error(getGqlErrorMessage(error));
            }
          },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };

  if (user?.billing?.productType === ProductType.OneTime) {
    return null;
  }

  if (isEmpty) {
    return (
      <Box>
        <Typography variant="bold14">등록된 결제 수단</Typography>
        <DotBox>
          <Typography variant="bold16" color={palette.gray400}>
            등록된 결제 수단이 없습니다.
          </Typography>
        </DotBox>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="bold14">등록된 결제 수단</Typography>
      <GrayBox>
        <CardLogo src={getCardLogo(user.billing?.cardCompany)} />
        <Typography variant="bold16">
          {getCardName(user.billing?.cardCompany)} (
          {getCardShortNumber(user.billing?.cardNumber)})
        </Typography>
        <FlexFullGrow />
        <Button
          size="small"
          color="black"
          variant="outline"
          disabled={isPending}
          onClick={changeCardInfo}
        >
          {isPending ? '변경 요청 중...' : '변경하기'}
        </Button>
      </GrayBox>
    </Box>
  );
};

const DotBox = styled(DashRoundedBox)`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const GrayBox = styled(GrayRoundedBox)`
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 8px;
`;

const CardLogo = styled(BackgroundImage).withConfig({
  shouldForwardProp: interceptProps(['src']),
})<{
  src?: string;
}>`
  width: 48px;
  height: 48px;
  border-radius: 100%;
  background-color: white;
  background-image: url(${({src}) => src}), url(${DefaultCard});
`;
